/**
 * ReflectionScreen — Private diary. Autosaves text, supports voice/photo additions.
 *
 * UI-INV-005: Leaf component — props only, no engine imports.
 * reflections-are-local-only invariant: text is localStorage-first; media uploads on explicit Save.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./ReflectionScreen.module.css";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";
import { TEST_IDS } from "../testing/testIds";
import { STORAGE_KEYS, TIMING, LIMITS } from "../constants";

function formatIntentEcho(intentLabels: string[]): string {
  if (intentLabels.length === 0) return "your practice";
  if (intentLabels.length === 1) return `your ${intentLabels[0]}`;
  return `your ${intentLabels[0]}, and with ${intentLabels[1]}`;
}

function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
}

function formatSavedAgo(savedAt: number | null): string {
  if (savedAt === null) return "";
  const ageSecs = Math.floor((Date.now() - savedAt) / 1000);
  if (ageSecs < 10) return "saved just now";
  return `saved ${ageSecs}s ago`;
}

type ReflectionData = {
  text: string;
  audioBlobs: Blob[];
  photo: File | null;
  savedAt: number | null;
};

type Props = {
  sessionId: string;
  intentLabels: string[];
  whisperLine: string | null;
  date: string;
  audioReflectionEnabled: boolean;
  onSave: (text: string, photo: File | null) => void;
  onClose: () => void;
};

export function ReflectionScreen({
  sessionId,
  intentLabels,
  whisperLine,
  date,
  audioReflectionEnabled,
  onSave,
  onClose,
}: Props) {
  const draftKey = `${STORAGE_KEYS.REFLECTION_DRAFT}_${sessionId}`;

  const [data, setData] = useState<ReflectionData>(() => ({
    text: localStorage.getItem(draftKey) ?? "",
    audioBlobs: [],
    photo: null,
    savedAt: null,
  }));
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [savedDisplay, setSavedDisplay] = useState("");
  const [photoWarning, setPhotoWarning] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState<string | null>(null);
  const [mediaUnavailable, setMediaUnavailable] = useState(
    !navigator.mediaDevices?.getUserMedia,
  );

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedAgoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingStartRef = useRef<number>(0);

  // Autosave: debounced 800ms after last keystroke
  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;
      setData((d) => ({ ...d, text: newText }));
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        localStorage.setItem(draftKey, newText);
        const now = Date.now();
        setSavedAt(now);
        setSavedDisplay("saved just now");
      }, TIMING.REFLECTION_AUTOSAVE_DEBOUNCE_MS);
    },
    [draftKey],
  );

  // "saved Ns ago" ticker
  useEffect(() => {
    savedAgoTimerRef.current = setInterval(() => {
      setSavedDisplay(formatSavedAgo(savedAt));
    }, TIMING.REFLECTION_SAVED_TICKER_MS);
    return () => {
      if (savedAgoTimerRef.current) clearInterval(savedAgoTimerRef.current);
    };
  }, [savedAt]);

  // Cleanup debounce + media on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // Voice note
  const handleVoicePointerDown = useCallback(async () => {
    if (mediaUnavailable) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const durationSecs = Math.floor((Date.now() - recordingStartRef.current) / 1000);
        const mins = Math.floor(durationSecs / 60);
        const secs = durationSecs % 60;
        setRecordingDuration(`${mins}:${secs.toString().padStart(2, "0")}`);
        setData((d) => ({ ...d, audioBlobs: [...d.audioBlobs, blob] }));
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      };
      recordingStartRef.current = Date.now();
      recorder.start();
      setIsRecording(true);
    } catch {
      setMediaUnavailable(true);
    }
  }, [mediaUnavailable]);

  const handleVoicePointerUp = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  // Single photo
  const handlePhotoSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > LIMITS.PHOTO_MAX_BYTES) {
        setPhotoWarning(`Photo too large (max ${LIMITS.PHOTO_MAX_MB} MB)`);
        return;
      }
      setPhotoWarning(null);
      setData((d) => ({ ...d, photo: file }));
    },
    [],
  );

  const handleSave = useCallback(() => {
    localStorage.setItem(draftKey, data.text);
    onSave(data.text, data.photo);
  }, [draftKey, data.text, data.photo, onSave]);

  const intentEchoText = formatIntentEcho(intentLabels);
  const words = wordCount(data.text);

  return (
    <div className={styles.screen} data-testid={TEST_IDS.REFLECTION_SCREEN}>
      {/* MetaStrip */}
      <div className={styles.meta}>
        <span className={styles.dateLabel}>Diary · {date}</span>
      </div>

      {/* Header */}
      <div className={styles.header}>
        <span className={styles.eyebrow}>You sat with</span>
        <h1 className={styles.heading}>{intentEchoText}.</h1>
        {whisperLine && <p className={styles.whisper}>{whisperLine}</p>}
      </div>

      {/* Diary textarea */}
      <div className={styles.textareaWrapper}>
        <textarea
          className={styles.textarea}
          value={data.text}
          onChange={handleTextChange}
          placeholder="Write anything. The words don't have to be right."
          aria-label="Diary"
          data-testid={TEST_IDS.REFLECTION_TEXTAREA}
        />
        <div className={styles.textMeta} data-testid={TEST_IDS.REFLECTION_WORD_COUNT}>
          {words > 0 && <span>{words} words</span>}
          {savedAt !== null && <span>{savedDisplay}</span>}
        </div>
      </div>

      {/* Additions row */}
      <div className={styles.additions}>
        {/* Voice note — only shown when audioReflectionEnabled is on */}
        {audioReflectionEnabled && (
          <div
            className={[styles.tile, isRecording ? styles.tileRecording : ""].join(" ")}
            onPointerDown={handleVoicePointerDown}
            onPointerUp={handleVoicePointerUp}
            onPointerLeave={handleVoicePointerUp}
            data-testid={TEST_IDS.REFLECTION_VOICE_TILE}
            role="button"
            aria-label="Voice note — hold to record"
          >
            {mediaUnavailable ? (
              <span className={styles.tileLabel}>Not available</span>
            ) : (
              <>
                <span className={styles.tileLabel}>Voice note</span>
                <span className={styles.tileSub}>
                  {recordingDuration ?? (isRecording ? "recording…" : "hold")}
                </span>
              </>
            )}
          </div>
        )}

        {/* Single photo */}
        <label className={styles.tile} data-testid={TEST_IDS.REFLECTION_PHOTO_TILE}>
          <span className={styles.tileLabel}>
            {data.photo ? data.photo.name : "Add a photo"}
          </span>
          {data.photo && <span className={styles.tileSub}>✓</span>}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className={styles.hiddenInput}
            onChange={handlePhotoSelect}
          />
        </label>
      </div>

      {photoWarning && <p className={styles.photoWarning}>{photoWarning}</p>}

      {/* Footer actions */}
      <div className={styles.actions}>
        <PrimaryAction onClick={handleSave} testId={TEST_IDS.REFLECTION_SAVE_DIARY}>Save reflection</PrimaryAction>
        <SecondaryAction onClick={onClose} testId={TEST_IDS.REFLECTION_CLOSE}>Skip</SecondaryAction>
      </div>
    </div>
  );
}
